import { Flex, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useState, useContext } from "react";
import { useLazyQuery } from "@apollo/client";
import AnnouncementCard from "./components/AnnouncementCard";
import CreateAnnouncementModal from "./components/CreateAnnouncementModal";
import { GET_ANNOUNCEMENTS_SENT_TO_PARTICIPANTS } from "../../../gql/announcementRequests";
import GreenOutlineButton from "../../../ui/buttons/GreenOutlineButton";
import OrangeButton from "../../../ui/buttons/OrangeButton";
import { AdminContext } from "../../AdminContext";
import LoadingScreen from "../../../ui/screens/LoadingScreen";
import ErrorScreen from "../../../ui/screens/ErrorScreen";
import UnderlineButton from "../../../ui/buttons/UnderlineButton";
import { getRoomString } from "../../../helpers/stringUtils";
import { formatDateV3 } from "../../../helpers/formatDateTime";
import { Plus } from "../../../ui/icons/ActionIcons";

export default function AdminAnnouncementsPage() {
  const [create, setCreate] = useState(false);
  const [selectedButtons, setSelectedButtons] = useState<boolean[]>(
    new Array(10).fill(false)
  );

  const [
    getAllAnnouncements,
    {
      loading: getAllAnnouncementsLoading,
      error: getAllAnnouncementsError,
      data: getAllAnnouncementsData,
      refetch: refetchAllAnnouncements,
    },
  ] = useLazyQuery(GET_ANNOUNCEMENTS_SENT_TO_PARTICIPANTS);

  const { roomToParticipant } = useContext(AdminContext);

  useEffect(() => {
    const selectedRoomNumbers = selectedButtons
      .map((selected, index) => (selected ? index + 1 : null))
      .filter(Boolean) as number[];

    if (selectedRoomNumbers.length === 0) {
      const allPids = Object.values(roomToParticipant);
      getAllAnnouncements({
        variables: { pids: allPids },
      });
      return;
    }

    const selectedParticipantIds = selectedRoomNumbers
      .map((roomNumber) => roomToParticipant[roomNumber])
      .filter(Boolean) as number[];

    getAllAnnouncements({
      variables: { pids: selectedParticipantIds },
    });
  }, [selectedButtons, roomToParticipant]);

  const handleButtonClick = (id: number) => {
    setSelectedButtons((prevSelected: any) => {
      const newSelected = [...prevSelected];
      newSelected[id] = !newSelected[id];
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    setSelectedButtons(new Array(10).fill(true));
  };

  const handleDeselectAll = () => {
    setSelectedButtons(new Array(10).fill(false));
  };

  if (getAllAnnouncementsLoading) return <LoadingScreen />;
  if (getAllAnnouncementsError)
    return <ErrorScreen message={getAllAnnouncementsError.message} />;

  return (
    <Flex width="100%" flexDir="column" height="fit-content" gap="15px">
      <Flex
        width="100%"
        height="fit-content"
        alignItems="center"
        justifyContent="space-between"
      >
        <Flex alignItems="baseline" gap="15px">
          <Text textStyle="h2" color="brand.primaryDark" pl="3px">
            Announcements
          </Text>
          <Text textStyle="b2" color="text.light">
            Expires in 7 days
          </Text>
        </Flex>

        <OrangeButton
          icon={<Plus />}
          label="Create Announcement"
          action={() => setCreate(true)}
          is_active={create}
        />
      </Flex>

      <Flex alignItems="center" gap="15px">
        <Text textStyle="s1" color="text.medium" fontWeight={600}>
          Filters:
        </Text>
        <Flex alignItems="center" gap="4px">
          {selectedButtons.map((isSelected: boolean, index: number) => (
            <GreenOutlineButton
              key={index}
              label={"Room " + (index + 1)}
              action={() => handleButtonClick(index)}
              is_active={isSelected}
            />
          ))}
        </Flex>
        <UnderlineButton label="Select All" action={() => handleSelectAll()} />
        <UnderlineButton
          label="Deselect All"
          action={() => handleDeselectAll()}
        />
      </Flex>

      <Flex flexDir="column" gap="8px" mt="6px">
        {getAllAnnouncementsData?.getAnnouncementsSentToParticipants.map(
          (announcement: any) => (
            <AnnouncementCard
              key={announcement.aid}
              announcement_id={announcement.aid}
              room={getRoomString(announcement)}
              message={announcement.message}
              timestamp={formatDateV3(new Date(announcement.date))}
              priority={announcement.priority}
              refetch={refetchAllAnnouncements}
            />
          )
        )}
      </Flex>

      {create && (
        <CreateAnnouncementModal
          refetch={refetchAllAnnouncements}
          onClose={() => setCreate(false)}
        />
      )}
    </Flex>
  );
}
